import { locationPermissionAtom } from "@/atoms/recommendationAtom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useAtomValue } from "jotai";
import { MapPin, Settings } from "lucide-react";

interface LocationErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LocationErrorDialog = ({ open, onOpenChange }: LocationErrorDialogProps) => {
  const locationPermission = useAtomValue(locationPermissionAtom);

  const handleRetryLocation = () => {
    if ((window as any).requestLocationPermission) {
      (window as any).requestLocationPermission();
    }
  };

  const getErrorContent = () => {
    if (!locationPermission.error) {
      return {
        title: "位置情報が必要です",
        description: "スポットの推薦には位置情報が必要です。位置情報を有効にしてください。",
        icon: <MapPin className="h-16 w-16 text-orange-500" />
      };
    }

    switch (locationPermission.error.type) {
      case "PERMISSION_DENIED":
        return {
          title: "位置情報の利用が拒否されています",
          description:
            "スポットの推薦には位置情報が必要です。ブラウザの設定で位置情報を有効にしてください。",
          icon: <MapPin className="h-16 w-16 text-red-500" />
        };
      case "NOT_SUPPORTED":
        return {
          title: "位置情報がサポートされていません",
          description:
            "お使いのブラウザでは位置情報がサポートされていません。対応ブラウザをご利用ください。",
          icon: <MapPin className="h-16 w-16 text-gray-500" />
        };
      case "POSITION_UNAVAILABLE":
      case "TIMEOUT":
        return {
          title: "位置情報を取得できませんでした",
          description:
            "位置情報の取得に失敗しました。GPS機能がオンになっているか確認してください。",
          icon: <MapPin className="h-16 w-16 text-orange-500" />
        };
      default:
        return {
          title: "位置情報の取得に失敗しました",
          description: "予期しないエラーが発生しました。再度お試しください。",
          icon: <MapPin className="h-16 w-16 text-red-500" />
        };
    }
  };

  const errorContent = getErrorContent();
  const canRetry = locationPermission.error?.type !== "NOT_SUPPORTED";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-50">
            {errorContent.icon}
          </div>
          <DialogTitle className="text-lg font-semibold">{errorContent.title}</DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {errorContent.description}
          </DialogDescription>
        </DialogHeader>

        {locationPermission.error?.type === "PERMISSION_DENIED" && (
          <div className="rounded-lg bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <Settings className="mt-0.5 h-5 w-5 text-blue-600" />
              <div className="text-sm">
                <p className="mb-1 font-medium text-blue-900">設定方法：</p>
                <ol className="list-inside list-decimal space-y-1 text-blue-700">
                  <li>ブラウザのアドレスバー左側の🔒マークをクリック</li>
                  <li>「位置情報」を「許可」に変更</li>
                  <li>ページを再読み込み</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          {canRetry && (
            <Button onClick={handleRetryLocation}>
              {locationPermission.state === "checking" ? "取得中..." : "位置情報を有効にする"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
